import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import { 
  CreditCard, Loader2, Plus, Download, Trash2, QrCode, 
  CheckCircle2, Clock, Package, Eye
} from 'lucide-react';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminNFC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchCodes, setBatchCodes] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  const [generateForm, setGenerateForm] = useState({
    count: 100,
    batch_name: ''
  });

  // Check admin access
  useEffect(() => {
    if (user && !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      toast({
        title: "Access Denied",
        description: "Only admin can access this page",
        variant: "destructive"
      });
      navigate('/');
    }
  }, [user, navigate, toast]);

  // Load batches
  useEffect(() => {
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      loadBatches();
    }
  }, [user]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/admin/batches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBatches(data);
      }
    } catch (error) {
      console.error('Error loading batches:', error);
      toast({
        title: "Error",
        description: "Failed to load batches",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (generateForm.count < 1 || generateForm.count > 1000) {
      toast({
        title: "Invalid Count",
        description: "Count must be between 1 and 1000",
        variant: "destructive"
      });
      return;
    }
    
    setGenerating(true);
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/admin/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(generateForm)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate QR codes');
      }
      
      toast({
        title: "Success!",
        description: `Generated ${data.count} QR codes`
      });
      
      setShowGenerateDialog(false);
      setGenerateForm({ count: 100, batch_name: '' });
      loadBatches();
      
    } catch (error) {
      console.error('Error generating QR codes:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate QR codes",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleViewBatch = async (batch) => {
    setSelectedBatch(batch);
    setShowBatchDialog(true);
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/admin/batch/${batch.batch_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBatchCodes(data.codes);
      }
    } catch (error) {
      console.error('Error loading batch codes:', error);
    }
  };

  const handleExportBundle = async (batch) => {
    try {
      toast({
        title: "Preparing download...",
        description: "Generating Excel and QR images bundle"
      });
      
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/admin/batch/${batch.batch_id}/export/bundle`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use batch name as filename
        const safeBatchName = batch.batch_name.replace(/\s+/g, '_').replace(/[\/\\]/g, '-');
        a.download = `${safeBatchName}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast({
          title: "Downloaded",
          description: "Bundle with Excel and QR images downloaded successfully"
        });
      } else {
        throw new Error('Failed to generate bundle');
      }
    } catch (error) {
      console.error('Error exporting bundle:', error);
      toast({
        title: "Error",
        description: "Failed to export bundle",
        variant: "destructive"
      });
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Delete all unclaimed QR codes in this batch? Claimed codes will be preserved.')) {
      return;
    }
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/admin/batch/${batchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Deleted",
          description: data.message
        });
        loadBatches();
      }
    } catch (error) {
      console.error('Error deleting batch:', error);
      toast({
        title: "Error",
        description: "Failed to delete batch",
        variant: "destructive"
      });
    }
  };

  if (!user || !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
    return null;
  }

  return (
    <Layout>
      <AdminNav />
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-6">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="bg-white shadow-sm rounded-lg mb-6">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <QrCode className="w-6 h-6 text-amber-600" />
                <div>
                  <h1 className="text-lg font-bold text-gray-900">NFC Card Management</h1>
                  <p className="text-xs text-gray-600">Generate and manage QR codes for NFC cards</p>
                </div>
              </div>
              <Button
                onClick={() => setShowGenerateDialog(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate QR Codes
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{batches.length}</p>
                    <p className="text-sm text-gray-600">Total Batches</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {batches.reduce((sum, b) => sum + b.unclaimed, 0)}
                    </p>
                    <p className="text-sm text-gray-600">Unclaimed Codes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {batches.reduce((sum, b) => sum + b.claimed, 0)}
                    </p>
                    <p className="text-sm text-gray-600">Claimed Codes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Batches List */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading batches...</p>
            </div>
          ) : batches.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No QR Codes Yet</h3>
                <p className="text-gray-600 mb-4">Generate your first batch of QR codes for NFC cards</p>
                <Button onClick={() => setShowGenerateDialog(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Generate QR Codes
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {batches.map((batch) => (
                <Card key={batch.batch_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-lg flex items-center justify-center">
                          <QrCode className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{batch.batch_name}</h3>
                          <p className="text-sm text-gray-500">
                            Created: {new Date(batch.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-lg font-bold text-gray-900">{batch.total}</p>
                          <p className="text-xs text-gray-500">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-600">{batch.claimed}</p>
                          <p className="text-xs text-gray-500">Claimed</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-amber-600">{batch.unclaimed}</p>
                          <p className="text-xs text-gray-500">Available</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewBatch(batch)}
                            title="View Codes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportBundle(batch)}
                            title="Download Excel + QR Images"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteBatch(batch.batch_id)}
                            className="text-red-600 hover:bg-red-50"
                            title="Delete Batch"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate QR Codes</DialogTitle>
            <DialogDescription>
              Create a new batch of QR codes for NFC card printing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="batch_name">Batch Name</Label>
              <Input
                id="batch_name"
                value={generateForm.batch_name}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, batch_name: e.target.value }))}
                placeholder="e.g., January 2025 Print Run"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="count">Number of QR Codes</Label>
              <Input
                id="count"
                type="number"
                min={1}
                max={1000}
                value={generateForm.count}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, count: parseInt(e.target.value) || 100 }))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 1000 codes per batch</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={generating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Generate {generateForm.count} Codes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Codes Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedBatch?.batch_name}</DialogTitle>
            <DialogDescription>
              {batchCodes.length} QR codes in this batch
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
              {batchCodes.map((code) => (
                <div 
                  key={code.id}
                  className={`p-4 rounded-lg border ${
                    code.status === 'claimed' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono font-bold text-sm">{code.code}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      code.status === 'claimed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {code.status === 'claimed' ? '✓ Activated' : 'Available'}
                    </span>
                  </div>
                  
                  {/* Card URL - Always show /c/{code} format */}
                  <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Card URL:</p>
                    <a 
                      href={`/c/${code.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono break-all"
                    >
                      /c/{code.code}
                    </a>
                  </div>
                  
                  {/* Show linked profile info for claimed cards */}
                  {code.status === 'claimed' && code.linked_username && (
                    <div className="mt-2 p-2 bg-green-100 rounded border border-green-200">
                      <p className="text-xs text-green-600 mb-1">Linked to:</p>
                      <a 
                        href={`/bio/${code.linked_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-700 hover:text-green-900 hover:underline font-medium"
                      >
                        {code.linked_name || code.linked_username}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowBatchDialog(false)}>
              Close
            </Button>
            <Button onClick={() => handleExportBundle(selectedBatch)} className="bg-amber-600 hover:bg-amber-700">
              <Download className="w-4 h-4 mr-2" />
              Download All (Excel + QR)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminNFC;
